import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';

import './App.css';
import Stat from './Stat';
import CurrencyInputs from './CurrencyInputs';
import Value from './Value';
import SliderValue from './SliderValue';
import { Stack } from '@blockstack/ui';
import Result from './Result';
import Header from './Header';
import mediaQueries from './mediaQueries';
interface BinanceTicker {
    symbol: string;
    price: string;
}

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});
const ceil = (num: number, pow: number) => {
    return Math.ceil(num / pow) * pow;
};

const round = (num: number, pow: number) => {
    return Math.round((num + Number.EPSILON) * Math.pow(10, pow)) / Math.pow(10, pow);
};
const floor = (num: number, pow: number) => {
    return Math.floor((num + Number.EPSILON) * Math.pow(10, pow)) / Math.pow(10, pow);
};

const TOTAL_SLOTS_AVAILABLE_PER_CYCLE = 4000;
const DEFAULT_STX_THRESHOLD_CEIL = 10000;
const NUMBER_OF_SLOTS_PER_REWARD_CYCLE = 2;
const POX_TRANSACTION_SIZE_ON_BTC_CHAIN = 250 + 33 * (NUMBER_OF_SLOTS_PER_REWARD_CYCLE + 1);
const MINING_REWARD_IN_STX = 1000;
const REWARD_CYCLE_LENGTH = 2000;
const AVERAGE_BTC_BLOCKS_PER_DAY = 144; //
const LENGTH_OF_REWARD_CYCLE = round(REWARD_CYCLE_LENGTH / AVERAGE_BTC_BLOCKS_PER_DAY, 2);

const Content = styled.div`
    max-width: 1200px;
    display: grid;
    margin: 0 auto;
    justify-content: center;
    background-color: #fff;
    color: #444;
    grid-template-columns: 1fr;
    padding: 16px;

    @media ${mediaQueries.laptop} {
        grid-template-columns: minmax(auto, 550px) minmax(auto, 400px);
        grid-gap: 30px;
    }
`;

const ResultContainer = styled.div`
    order: -1;

    @media ${mediaQueries.laptop} {
        order: 0;
    }
`;

function numberWithCommas(x: number) {
    var parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

const getStackingThreshold = (totalLiquidSTX: number, totalStackedSTX: number) => {
    const percentage = (totalStackedSTX / totalLiquidSTX) * 100;
    let threshold = 0;
    if (percentage < 25) {
        threshold = (totalLiquidSTX / TOTAL_SLOTS_AVAILABLE_PER_CYCLE) * 0.25;
    } else {
        threshold = (totalLiquidSTX / TOTAL_SLOTS_AVAILABLE_PER_CYCLE) * (percentage / 100);
    }
    threshold = ceil(threshold, DEFAULT_STX_THRESHOLD_CEIL);
    return Math.floor(threshold);
};

export const App: React.FC = () => {
    const getUserStackedUSD = (userStackedSTX: number, stxPrice: number): number => {
        return round(userStackedSTX * stxPrice, 0);
    };

    const onSTXPriceChange = (price: number) => {
        const userStackedUSD = getUserStackedUSD(userStackedSTX, price);
        setUserStackedUSD(userStackedUSD);
        setStxPrice(price);
    };

    const onUserStackedSTXChange = (amountOfStackedStx: number) => {
        const userStackedUSD = getUserStackedUSD(amountOfStackedStx, stxPrice);
        setUserStackedUSD(round(userStackedUSD, 2));
        setUserStackedSTX(amountOfStackedStx);
    };

    const onUserStackedUSDChange = (amountOfUSD: number) => {
        const userStackedSTX = amountOfUSD / stxPrice;
        setUserStackedSTX(round(userStackedSTX, 0));
        setUserStackedUSD(amountOfUSD);
    };

    // Set State
    const [isLoading, setIsLoading] = useState(true);
    const [atLeastLaptopQuery, setAtLeastLaptopQuery] = useState(false);
    const [liquidSTX, setLiquidSTX] = useState(0);
    const [userStackedSTX, setUserStackedSTX] = useState(1200000);
    const [totalStackedSTX, setTotalStackedSTX] = useState(0);
    const [totalStackedSTXPercentage, setTotalStackedSTXPercentage] = useState(50);
    const [stxPrice, setStxPrice] = useState(0);
    const [btcPrice, setBtcPrice] = useState(0);
    const [showAdvancedSettings, toggleAdvancedSettings] = useState(false);
    const [userStackedUSD, setUserStackedUSD] = useState(0);
    const [btcTransactionFee, setBtcTransactionFee] = useState(0);
    const [numberOfMiners, setNumberOfMiners] = useState(10);
    const [minersShare, setMinersShare] = useState(15); // Percentage
    const [stxTransactionFees, setStxTransactionFees] = useState(100); // STX
    const [annualStackerRewardPercentage, setAnnualStackerRewardPercentage] = useState(0);

    // Calculation
    const minimumStakeStx = getStackingThreshold(Number(liquidSTX), totalStackedSTX);
    const userAllowedRewardAddresses = Math.floor(userStackedSTX / minimumStakeStx);
    // Costs
    const transactionCostPerMinerPerBlock = round(
        0.00000001 * btcPrice * btcTransactionFee * POX_TRANSACTION_SIZE_ON_BTC_CHAIN,
        2,
    );
    const totalBtcTransactionsCosts = round(numberOfMiners * transactionCostPerMinerPerBlock, 2);
    // Gross Income
    const totalReward = round((stxTransactionFees + MINING_REWARD_IN_STX) * stxPrice, 2);
    const excessValue = totalReward - totalBtcTransactionsCosts;
    const minersShareUSD = round(excessValue * (minersShare / 100), 2);
    const stackersShare = excessValue - minersShareUSD;
    const rewardPerStackerAddress = round(stackersShare / NUMBER_OF_SLOTS_PER_REWARD_CYCLE, 4);

    // Net Income
    const annualStackerRewardInBTC = round(
        (rewardPerStackerAddress * userAllowedRewardAddresses * 365) / LENGTH_OF_REWARD_CYCLE / btcPrice,
        4,
    );
    const annualStackerRewardInUSD = round(annualStackerRewardInBTC * btcPrice, 2);

    useEffect(() => {
        setTotalStackedSTX(liquidSTX * (totalStackedSTXPercentage / 100));
    }, [totalStackedSTXPercentage]);

    useEffect(() => {
        setAnnualStackerRewardPercentage(round((annualStackerRewardInUSD / userStackedUSD) * 100, 2));
    }, [userStackedUSD, annualStackerRewardInUSD]);

    useEffect(() => {
        const fetchCryptoPrices = async () => {
            try {
                const data = await fetch('https://api.binance.com/api/v3/ticker/price');
                const json = await data.json();

                const stxTicker = json.find((ticker: BinanceTicker) => {
                    return ticker.symbol === 'STXUSDT';
                });

                const btcTicker = json.find((ticker: BinanceTicker) => {
                    return ticker.symbol === 'BTCUSDT';
                });

                const stxPrice = floor(Number(stxTicker.price), 3);
                setStxPrice(stxPrice);
                setBtcPrice(round(Number(btcTicker.price), 2));
                const userStackedInUSD = getUserStackedUSD(userStackedSTX, stxPrice);
                const stxData = await fetch('https://explorer-api.blockstack.org/api/v2/total-supply');
                const stxDataJSON = await stxData.json();
                const unlockedSupply = parseInt(stxDataJSON.unlockedSupply, 10);

                const bitcoinTransactionFeesRequest = await fetch(
                    'https://bitcoinfees.earn.com/api/v1/fees/recommended',
                );
                const bitcoinTransactionFees = await bitcoinTransactionFeesRequest.json();
                const halfHourFee = bitcoinTransactionFees.halfHourFee;

                setLiquidSTX(unlockedSupply);
                setTotalStackedSTX(unlockedSupply * (totalStackedSTXPercentage / 100));
                setUserStackedUSD(userStackedInUSD);
                setBtcTransactionFee(halfHourFee);
            } catch (error) {
                setIsLoading(false);
                console.log(error);
            }

            setIsLoading(false);
        };

        const checkWidth = () => {
            const match = window.matchMedia(`${mediaQueries.laptop}`);
            setAtLeastLaptopQuery(match.matches);
        };

        checkWidth();
        window.addEventListener('resize', checkWidth);

        fetchCryptoPrices();
    }, []);

    return (
        <div>
            <Header
                showAdvancedSettings={showAdvancedSettings}
                onToggle={(event) => {
                    toggleAdvancedSettings(!showAdvancedSettings);
                }}
            />

            <Content>
                {isLoading ? (
                    <div></div>
                ) : (
                    <React.Fragment>
                        <div>
                            <Stat title="" marginTop={atLeastLaptopQuery ? '-100px' : ''}>
                                <CurrencyInputs
                                    label={'Amount of STX you’ll lock'}
                                    firstInputValue={userStackedSTX}
                                    secondInputValue={userStackedUSD}
                                    onFirstInputChange={onUserStackedSTXChange}
                                    onSecondInputChange={onUserStackedUSDChange}
                                    primaryInputCurrency="STX"
                                    secondaryInputCurrency="USD"
                                    min={minimumStakeStx}
                                    max={totalStackedSTX}
                                    tooltip={'Amount of STX tokens stacked by you'}
                                />
                                <div
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                ></div>
                            </Stat>

                            <CSSTransition in={showAdvancedSettings} classNames="example" timeout={600} unmountOnExit>
                                <div>
                                    <Stat title="">
                                        <SliderValue
                                            value={stxPrice}
                                            label="Stacks Token Price"
                                            onChange={onSTXPriceChange}
                                            min={0.01}
                                            max={1}
                                            step={0.1}
                                            tooltip={'Price of STX token in USD'}
                                            maxLength={4}
                                            key={1}
                                            currencyLabel="USD"
                                        />
                                        <SliderValue
                                            value={btcPrice}
                                            label="Bitcoin Price"
                                            onChange={setBtcPrice}
                                            min={1000}
                                            max={30000}
                                            step={100}
                                            tooltip={'Price of Bitcoin in USD'}
                                            key={2}
                                            style={{ marginTop: '40px' }}
                                            currencyLabel="USD"
                                        />
                                    </Stat>
                                    <Stat title="">
                                        <SliderValue
                                            value={totalStackedSTXPercentage}
                                            label="Total stacked STX"
                                            onChange={setTotalStackedSTXPercentage}
                                            min={1}
                                            max={100}
                                            step={1}
                                            tooltip={'Total amount of stacked STX tokens in the system'}
                                            key={3}
                                            currencyLabel="%"
                                        />
                                        <SliderValue
                                            value={stxTransactionFees}
                                            label="STX Transaction Fees"
                                            onChange={setStxTransactionFees}
                                            min={20}
                                            max={400}
                                            step={20}
                                            tooltip={'Amount of transactions fees paid to the network'}
                                            key={4}
                                            currencyLabel="STX"
                                            style={{ marginTop: '40px' }}
                                        />
                                    </Stat>
                                    <Stat title="">
                                        <SliderValue
                                            value={minersShare}
                                            label="Miner's Share"
                                            onChange={setMinersShare}
                                            min={0}
                                            max={100}
                                            step={1}
                                            tooltip={"Expected miner's share"}
                                            currencyLabel="%"
                                        />
                                        <SliderValue
                                            value={numberOfMiners}
                                            label="Number of Miners or Mining Pools"
                                            onChange={setNumberOfMiners}
                                            min={1}
                                            max={floor(totalReward / transactionCostPerMinerPerBlock, 0)}
                                            step={1}
                                            tooltip={
                                                'Expected number of miners or mining pools participating in mining. Maximum number is limited by profitability of mining.'
                                            }
                                            style={{ marginTop: '40px' }}
                                            currencyLabel="#"
                                        />
                                    </Stat>
                                </div>
                            </CSSTransition>

                            <Stack flexDirection="column">
                                <Stat title="STX Network">
                                    <Stack style={{ display: 'flex', marginRight: '20px', width: '100%' }}>
                                        <Value
                                            label="Total STX Supply"
                                            value={numberWithCommas(liquidSTX)}
                                            tooltip="Total Liquid Supply of STX Tokens"
                                        />
                                        <Value
                                            label="Stacking Threshold"
                                            value={`${numberWithCommas(minimumStakeStx)}`}
                                            tooltip="Minimal amount of STX tokens required for participation in stacking."
                                        />
                                        <CSSTransition
                                            in={showAdvancedSettings}
                                            classNames="example"
                                            timeout={600}
                                            unmountOnExit
                                        >
                                            {showAdvancedSettings && (
                                                <div>
                                                    <Value
                                                        label="All Reward Addresses"
                                                        value={numberWithCommas(TOTAL_SLOTS_AVAILABLE_PER_CYCLE)}
                                                        tooltip="Total number of addresses eligible for stacking reward."
                                                    />
                                                    <Value
                                                        label="Reward Per Address"
                                                        value={rewardPerStackerAddress}
                                                        tooltip={'Reward per one stacking address in Bitcoin'}
                                                    />
                                                    <Value
                                                        label="# of your Reward Addresses"
                                                        value={userAllowedRewardAddresses}
                                                        tooltip={'Maximum number of address for your payout'}
                                                    />
                                                </div>
                                            )}
                                        </CSSTransition>
                                    </Stack>
                                </Stat>
                            </Stack>
                        </div>
                        <ResultContainer>
                            <Result
                                arr={annualStackerRewardPercentage}
                                value={formatter.format(annualStackerRewardInUSD)}
                                userBitcoinReward={annualStackerRewardInBTC}
                                totalStxSupply={numberWithCommas(liquidSTX)}
                                stackingThreshold={`${numberWithCommas(minimumStakeStx)}`}
                                nrRewardsAddresses={numberWithCommas(TOTAL_SLOTS_AVAILABLE_PER_CYCLE)}
                                nrUserRewardAddresses={userAllowedRewardAddresses}
                                rewardPerAddress={formatter.format(rewardPerStackerAddress)}
                                showAdvancedSettings={showAdvancedSettings}
                                atLeastLaptopQuery={atLeastLaptopQuery}
                            />
                        </ResultContainer>
                    </React.Fragment>
                )}
            </Content>
        </div>
    );
};
interface AppState {
    [key: string]: any;
}
